<template>
  <div>
    <div v-for="(result, index) in votingResults" :key="result.key || `index-${index}`">
      <div class="mt-8 flex items-center gap-x-5">
        <div class="typo-h-5 shrink-0 text-white">
          {{ result.value?.split(':')[0] || result.key }}
        </div>
        <!-- eslint-disable readable-tailwind/multiline -->
        <div class="relative flex h-13 w-full items-center overflow-hidden rounded-bl-2xl bg-white/10">
          <!-- eslint-enable readable-tailwind/multiline -->
          <div
            :style="{
              width: isReady ? `${result.percentage}%` : '0%',
              transitionDuration: `${result.percentage * 20}ms`,
            }"
            class="h-full transition-all ease-linear"
            :class="isReady ? 'bg-white' : 'bg-white/50'"
          />
          <div
            class="typo-h-5 absolute px-4 transition-all duration-200 ease-out"
            :style="{
              left: `${result.percentage}%`,
              transitionDelay: `${(result.percentage * 20) + 10}ms`,
            }"
            :class="[
              result.percentage > 86 ? '-translate-x-full text-red-500' : `
                text-white
              `,
              isReady ? 'opacity-100' : '-translate-x-20 opacity-0',
            ]"
          >
            {{ result.percentage }}%
          </div>
        </div>
      </div>
    </div>
    <div v-if="isLoading" class="h-60" />
  </div>
</template>

<script setup lang="ts">
import type { VotingResultFragment } from '#gql'

type VotingResultWithPercentage = VotingResultFragment & { percentage: number }

const props = defineProps<{
  formHandle: string
  translatedFormHandles?: string[]
}>()

const isLoading = ref<boolean>(true)
const isReady = ref<boolean>(false)
const votingResults = ref<VotingResultWithPercentage[]>([])
const GqlInstance = useGql()

async function fetchVotingResults() {
  const loadingTimeout = setTimeout(() => {
    isLoading.value = true
  }, 500)
  const payload = await GqlInstance('VotingResults', {
    form_handle: props.formHandle,
    translated_form_handles: props.translatedFormHandles,
  })

  clearTimeout(loadingTimeout)
  isLoading.value = false

  if (!payload?.votingResults) {
    console.error('Voting results could not be fetched')
    return
  }

  const results = payload.votingResults as VotingResultFragment[] || []

  votingResults.value = getPercetages(results)
}

function getPercetages(votingResults: VotingResultFragment[]) {
  const totalVotes = votingResults.reduce(
    (acc, curr) => acc + (curr.votes ?? 0),
    0,
  )
  return votingResults.map(result => ({
    ...result,
    percentage: Math.round(totalVotes > 0 ? ((result.votes ?? 0) / totalVotes) * 100 : 0),

  }))
}

watch(isLoading, async (newVal) => {
  if (newVal === false) {
    // Wait for DOM to update with new data
    await nextTick()
    // Small delay for visual effect, but much shorter
    setTimeout(() => {
      isReady.value = true
    }, 100)
  }
})

onMounted(() => {
  fetchVotingResults()
})
</script>
